import React, { useState, useEffect } from 'react';
import {
  CurrencyPoundIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  BanknotesIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';
import { payrollAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Payroll = () => {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // view, edit, generate
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employee_id: ''
  });
  const [generatePayrollData, setGeneratePayrollData] = useState({
    pay_period_start: '',
    pay_period_end: '',
    employee_ids: []
  });

  const { user, isEmployee, isManager, isAdmin } = useAuth();

  const fetchPayrollRecords = async () => {
    try {
      const response = await payrollAPI.getAll({
        month: filters.month,
        year: filters.year,
        employee_id: filters.employee_id
      });
      setPayrollRecords(response.data.payroll_records || []);
    } catch (error) {
      console.error('Error fetching payroll records:', error);
    }
  };

  const fetchPayrollSummary = async () => {
    try {
      const response = await payrollAPI.getSummary({
        month: filters.month,
        year: filters.year
      });
      setPayrollSummary(response.data);
    } catch (error) {
      console.error('Error fetching payroll summary:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchPayrollRecords(),
          fetchPayrollSummary()
        ]);
      } catch (error) {
        setError('Failed to load payroll data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    try {
      await payrollAPI.generate(generatePayrollData);
      setShowModal(false);
      setGeneratePayrollData({
        pay_period_start: '',
        pay_period_end: '',
        employee_ids: []
      });
      fetchPayrollRecords();
      fetchPayrollSummary();
      alert('Payroll generated successfully');
    } catch (error) {
      console.error('Error generating payroll:', error);
      alert(error.response?.data?.message || 'Failed to generate payroll');
    }
  };

  const handleDownloadPayslip = async (payrollId) => {
    try {
      const response = await payrollAPI.getPayslip(payrollId);
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-${payrollId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading payslip:', error);
      alert('Failed to download payslip');
    }
  };

  const openModal = (mode, record = null) => {
    setModalMode(mode);
    setSelectedRecord(record);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payroll Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage employee payroll and payslips</p>
        </div>
        {(isManager() || isAdmin()) && (
          <button
            onClick={() => openModal('generate')}
            className="btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Generate Payroll
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Month
            </label>
            <select
              className="form-input"
              value={filters.month}
              onChange={(e) => setFilters({
                ...filters,
                month: parseInt(e.target.value)
              })}
            >
              {months.map((month, index) => (
                <option key={index} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <select
              className="form-input"
              value={filters.year}
              onChange={(e) => setFilters({
                ...filters,
                year: parseInt(e.target.value)
              })}
            >
              {[2023, 2024, 2025].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {!isEmployee() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee (Optional)
              </label>
              <input
                type="text"
                placeholder="Search by employee name or ID"
                className="form-input"
                value={filters.employee_id}
                onChange={(e) => setFilters({
                  ...filters,
                  employee_id: e.target.value
                })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Payroll Summary */}
      {payrollSummary && (isManager() || isAdmin()) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Gross Pay
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(payrollSummary.total_gross_pay)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyPoundIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Net Pay
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(payrollSummary.total_net_pay)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalculatorIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Deductions
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(payrollSummary.total_deductions)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Employees Paid
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {payrollSummary.employee_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Records */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            {isEmployee() ? 'My Payslips' : 'Payroll Records'}
          </h2>
        </div>

        {payrollRecords.length === 0 ? (
          <div className="text-center py-8">
            <BanknotesIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payroll records</h3>
            <p className="text-gray-600">
              No payroll records found for the selected period
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {!isEmployee() && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pay Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gross Pay
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deductions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Pay
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payrollRecords.map((record) => (
                  <tr key={record.id}>
                    {!isEmployee() && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">
                            {record.first_name} {record.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {record.employee_number}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(record.pay_period_start)} - {formatDate(record.pay_period_end)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(record.gross_pay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {formatCurrency(
                        (parseFloat(record.income_tax) || 0) + 
                        (parseFloat(record.national_insurance_employee) || 0) + 
                        (parseFloat(record.pension_employee) || 0) + 
                        (parseFloat(record.student_loan) || 0) + 
                        (parseFloat(record.other_deductions) || 0)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(record.net_pay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        record.submitted_to_hmrc 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {record.submitted_to_hmrc ? 'Submitted' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openModal('view', record)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPayslip(record.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Download Payslip"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        </button>
                        {(isManager() || isAdmin()) && (
                          <button
                            onClick={() => openModal('edit', record)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Record"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {modalMode === 'generate' ? 'Generate Payroll' :
                   modalMode === 'edit' ? 'Edit Payroll Record' : 'Payroll Details'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {modalMode === 'generate' ? (
                <form onSubmit={handleGeneratePayroll} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pay Period Start
                      </label>
                      <input
                        type="date"
                        required
                        className="form-input"
                        value={generatePayrollData.pay_period_start}
                        onChange={(e) => setGeneratePayrollData({
                          ...generatePayrollData,
                          pay_period_start: e.target.value
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pay Period End
                      </label>
                      <input
                        type="date"
                        required
                        className="form-input"
                        value={generatePayrollData.pay_period_end}
                        onChange={(e) => setGeneratePayrollData({
                          ...generatePayrollData,
                          pay_period_end: e.target.value
                        })}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Generate Payroll
                    </button>
                  </div>
                </form>
              ) : selectedRecord && (
                <div className="space-y-6">
                  {/* Employee Info */}
                  {!isEmployee() && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Employee</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {selectedRecord.first_name} {selectedRecord.last_name}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Employee Number</label>
                        <div className="mt-1 text-sm text-gray-900">{selectedRecord.employee_number}</div>
                      </div>
                    </div>
                  )}

                  {/* Pay Period */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pay Period Start</label>
                      <div className="mt-1 text-sm text-gray-900">
                        {formatDate(selectedRecord.pay_period_start)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pay Period End</label>
                      <div className="mt-1 text-sm text-gray-900">
                        {formatDate(selectedRecord.pay_period_end)}
                      </div>
                    </div>
                  </div>

                  {/* Earnings */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Earnings</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Basic Pay</label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {formatCurrency(selectedRecord.basic_pay)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Overtime Pay</label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {formatCurrency(selectedRecord.overtime_pay)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Bonus</label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {formatCurrency(selectedRecord.bonus)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">Gross Pay:</span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(selectedRecord.gross_pay)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Deductions</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Income Tax</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formatCurrency(selectedRecord.income_tax)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">National Insurance</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formatCurrency(selectedRecord.national_insurance_employee)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pension Contribution</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formatCurrency(selectedRecord.pension_employee)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Student Loan</label>
                        <div className="mt-1 text-sm text-gray-900">
                          {formatCurrency(selectedRecord.student_loan)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">Net Pay:</span>
                        <span className="font-bold text-primary-600">
                          {formatCurrency(selectedRecord.net_pay)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Close
                </button>
                {modalMode !== 'view' && modalMode !== 'generate' && (
                  <button className="btn-primary">
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;